import { motion } from 'framer-motion'
import { Send } from 'lucide-react'

function Cmd({ command }: { command: string }) {
    return (
        <p className="font-mono text-sm mb-6">
            <span className="text-primary font-bold">$</span>{' '}
            <span className="text-foreground">{command}</span>
        </p>
    )
}

const contactMethods = [
    {
        label: 'Email',
        value: 'raphealasomanikwabena@gmail.com',
        href: 'mailto:raphealasomanikwabena@gmail.com',
        note: 'Best for detailed project discussions.',
    },
    {
        label: 'LinkedIn',
        value: 'linkedin.com/in/younggem',
        href: 'https://www.linkedin.com/in/younggem',
        note: 'Connect for professional networking.',
    },
    {
        label: 'GitHub',
        value: 'github.com/Rapheal-Kwabena',
        href: 'https://github.com/Rapheal-Kwabena',
        note: 'See my code and open source work.',
    },
]

export default function ContactPage() {
    return (
        <div className="min-h-screen font-mono py-12">
            <div className="max-w-6xl mx-auto px-6 space-y-16">

                {/* ── Page header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <h1 className="text-sm mb-1">
                        <span className="text-primary">rapheal@portfolio</span>
                        <span className="text-[#444]">:~$</span>
                        <span className="text-foreground ml-2">contact --help</span>
                    </h1>
                    <p className="text-xs text-[#555]">// I respond to all messages within 24 hours.</p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-12">

                    {/* ── Contact methods ── */}
                    <motion.div
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1, duration: 0.5 }}
                    >
                        <Cmd command="contact --list" />
                        <div className="space-y-0">
                            {contactMethods.map(({ label, value, href, note }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target={href.startsWith('http') ? '_blank' : undefined}
                                    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                    className="flex gap-4 py-4 border-b border-[#111] last:border-0 group hover:bg-[#0a0a0a] px-2 -mx-2 rounded-sm transition-colors"
                                >
                                    <span className="text-primary text-xs shrink-0 mt-0.5">&gt;</span>
                                    <div className="text-xs">
                                        <p className="text-[#555] mb-0.5">{label}</p>
                                        <p className="text-foreground group-hover:text-primary transition-colors font-mono break-all">
                                            {value}
                                        </p>
                                        <p className="text-[#444] mt-1">{note}</p>
                                    </div>
                                </a>
                            ))}
                        </div>

                        {/* Availability */}
                        <div className="mt-8 term-card">
                            <p className="text-xs text-primary mb-3">$ systemctl status availability</p>
                            <div className="space-y-1.5 text-xs">
                                <div className="flex items-center gap-2">
                                    <span className="status-dot-online" />
                                    <span className="text-primary">Currently Available</span>
                                </div>
                                <p className="text-[#555]">Open to: Freelance &amp; Full-time roles</p>
                                <p className="text-[#555]">Response time: ~24 hours</p>
                                <p className="text-[#555]">Timezone: Central Time (CT, USA)</p>
                            </div>
                        </div>
                    </motion.div>

                    {/* ── Contact form ── */}
                    <motion.div
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <Cmd command="send-message --interactive" />
                        <div className="terminal-window">
                            <div className="terminal-titlebar">
                                <span className="terminal-dot bg-[#ff5f57]" />
                                <span className="terminal-dot bg-[#ffbd2e]" />
                                <span className="terminal-dot bg-[#28c840]" />
                                <span className="ml-3 text-[10px] text-[#444]">send-message.sh</span>
                            </div>
                            <form
                                action="https://api.web3forms.com/submit"
                                method="POST"
                                className="p-6 space-y-4"
                            >
                                <input type="hidden" name="access_key" value="58f1c041-7ab1-4b2e-a5db-96e667fb634f" />
                                <input type="hidden" name="subject" value="New message from raphealgadoh.me portfolio" />
                                <input type="checkbox" name="botcheck" className="hidden" />

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="contact-name" className="block text-[10px] text-[#555] mb-1.5 font-mono">
                                            --name <span className="text-primary">*</span>
                                        </label>
                                        <input
                                            id="contact-name"
                                            type="text"
                                            name="name"
                                            placeholder="your full name"
                                            required
                                            className="term-input"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="contact-email" className="block text-[10px] text-[#555] mb-1.5 font-mono">
                                            --email <span className="text-primary">*</span>
                                        </label>
                                        <input
                                            id="contact-email"
                                            type="email"
                                            name="email"
                                            placeholder="your@email.com"
                                            required
                                            className="term-input"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="contact-subject" className="block text-[10px] text-[#555] mb-1.5 font-mono">
                                        --subject <span className="text-primary">*</span>
                                    </label>
                                    <input
                                        id="contact-subject"
                                        type="text"
                                        name="subject"
                                        placeholder="e.g. Freelance project, Collaboration, Question..."
                                        required
                                        className="term-input"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="contact-message" className="block text-[10px] text-[#555] mb-1.5 font-mono">
                                        --message <span className="text-primary">*</span>
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        name="message"
                                        rows={5}
                                        placeholder="tell me about your project or idea..."
                                        required
                                        className="term-input resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn-term-accent w-full justify-center text-xs flex items-center gap-2 py-2.5"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                    [ send message ]
                                </button>
                            </form>
                        </div>
                    </motion.div>

                </div>
            </div>
        </div>
    )
}
